import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Printer } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import type { FixListEntry, Persona } from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import {
  canvasKeys,
  getSession,
  listSessionCaptures,
} from '../api/canvas-captures';
import { getRoomForCapture, roomKeys } from '../api/research-rooms';
import { printWhenReady } from '../lib/print-when-ready';
import { usePersonas } from '../persona/use-personas';
import { Wordmark } from '../research-report/wordmark';
import { useFixListEntries } from './fix-list';
import { LEVEL, RESOLVED_WORD, roomFixes } from './room-fix-list';

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * A room's fix list as a document to hand on: every change the room backs,
 * why, who said so in their own words, and what has been dealt with — laid
 * out for the page, and saved as a PDF by the browser's own print dialog.
 *
 * Opened from the fix list card's "Download PDF", which adds `?print=1` so
 * the dialog opens by itself once the list has loaded.
 */
export function FixListPrintPage() {
  const { captureId = '', sessionId = '' } = useParams();
  const [params] = useSearchParams();

  const session = useQuery({
    queryFn: () => getSession(sessionId),
    queryKey: canvasKeys.session(sessionId),
  });
  const captures = useQuery({
    queryFn: () => listSessionCaptures(sessionId),
    queryKey: canvasKeys.sessionCaptures(sessionId),
  });
  const view = useQuery({
    queryFn: () => getRoomForCapture(captureId),
    queryKey: roomKeys.forCapture(captureId),
  });
  const room = view.data?.room;
  const all = useFixListEntries(room?.projectId);
  // Who said what, as more than a name: the PDF is read by people who were
  // never in the room.
  const { loaded: panelLoaded, personas } = usePersonas({
    projectId: room?.projectId,
  });
  const panel = new Map(personas.map((persona) => [persona.id, persona]));
  const seated = room?.personaIds.length ?? 0;

  // The panel too, or the print dialog would open on quotes with no roles.
  const loaded = Boolean(room) && all !== undefined && panelLoaded;
  const entries = room && all ? roomFixes(all, room.id) : [];
  const open = entries.filter((entry) => entry.status === 'fix');
  const resolved = entries.filter((entry) => entry.status !== 'fix');
  const screen = captures.data?.find((capture) => capture.id === captureId);
  const title =
    session.data?.name ?? session.data?.label ?? screen?.name ?? 'Fix list';

  // Straight to the print dialog when that is what was asked for — once.
  const printed = useRef(false);
  const autoPrint = params.get('print') === '1';
  useEffect(() => {
    if (!autoPrint || !loaded || printed.current) return;
    printed.current = true;
    trackEvent('fix_list_printed', { source: 'auto' });
    void printWhenReady();
  }, [autoPrint, loaded]);

  return (
    <div className="min-h-svh bg-warm pb-20 [-webkit-print-color-adjust:exact] [print-color-adjust:exact] print:bg-surface print:pb-0">
      <nav className="sticky top-0 z-20 border-b border-hairline bg-warm/85 backdrop-blur print:hidden">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-3 px-4 sm:px-0">
          <Link
            className="flex h-8 items-center gap-1.5 rounded-button px-2 text-ui font-medium text-text-secondary transition hover:bg-surface-hover hover:text-text-primary"
            state={{ from: 'fix_list_print' }}
            to={`/sessions/${sessionId}`}
          >
            <ArrowLeft aria-hidden="true" className="size-4" />
            Back to the room
          </Link>
          <span className="ml-auto" />
          {loaded ? (
            <button
              className="flex h-8 cursor-pointer items-center gap-1.5 rounded-button bg-ink px-3 text-ui font-semibold text-white transition hover:bg-ink/90"
              onClick={() => {
                trackEvent('fix_list_printed', { source: 'button' });
                void printWhenReady();
              }}
              type="button"
            >
              <Printer aria-hidden="true" className="size-3.5" />
              Save as PDF
            </button>
          ) : null}
        </div>
      </nav>

      <main className="px-4 pt-8 sm:px-6 print:p-0">
        {!loaded ? (
          <div className="flex justify-center py-24">
            <Loader2
              aria-label="Loading the fix list"
              className="size-5 animate-spin text-text-muted"
            />
          </div>
        ) : (
          <article className="mx-auto max-w-3xl rounded-panel bg-surface px-6 py-10 shadow-soft ring-1 ring-hairline sm:px-12 sm:py-12 print:max-w-none print:rounded-none print:px-0 print:py-0 print:shadow-none print:ring-0">
            <header className="border-b border-hairline pb-8">
              <div className="flex items-center justify-between">
                <Wordmark />
                <p className="font-mono text-tag text-text-muted">
                  Fix list · {formatDate(new Date())}
                </p>
              </div>
              <h1 className="!mt-10 !mb-0 !font-report !text-report-title !font-medium !text-text-primary">
                {title}
              </h1>
              <p className="mt-3 text-ui text-text-secondary">
                {[
                  screen?.name,
                  session.data?.host,
                  `${String(open.length)} to fix`,
                  resolved.length > 0
                    ? `${String(resolved.length)} dealt with`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <p className="mt-4 text-ui leading-relaxed text-text-secondary">
                The changes most worth making to{' '}
                {screen?.name ? `the ${screen.name}` : 'this page'}
                {session.data?.host ? ` on ${session.data.host}` : ''}, from a
                feedback session in which{' '}
                {seated === 1
                  ? 'one simulated user'
                  : `${String(seated)} simulated users`}{' '}
                (AI personas built around the people this product is for) looked
                at the page and said what got in their way. Each fix says what
                to change, why, and who raised it, in their own words.
              </p>
            </header>

            {open.length === 0 ? (
              <p className="py-12 text-center text-ui text-text-secondary">
                Nothing on the list to fix.
              </p>
            ) : (
              <ol className="divide-y divide-hairline">
                {open.map((entry, index) => (
                  <Fix
                    entry={entry}
                    key={entry.id}
                    number={index + 1}
                    panel={panel}
                  />
                ))}
              </ol>
            )}

            {resolved.length > 0 ? (
              <section className="mt-10 break-inside-avoid">
                <h2 className="!m-0 !text-small-label !font-semibold !text-text-muted uppercase">
                  Dealt with
                </h2>
                <ul className="mt-3 space-y-2">
                  {resolved.map((entry) => (
                    <li
                      className="flex items-baseline gap-3 text-ui text-text-secondary"
                      key={entry.id}
                    >
                      <span className="w-24 shrink-0 text-caption text-text-muted">
                        {RESOLVED_WORD[entry.status]}
                      </span>
                      <span>
                        {entry.issue.recommendation.trim() ||
                          entry.issue.description}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <footer className="mt-12 border-t border-hairline pt-4 text-caption text-text-muted">
              From a Floto feedback room. The people quoted are simulated users,
              not real customers; their words are quoted as they said them.
            </footer>
          </article>
        )}
      </main>
    </div>
  );
}

/** One fix: the change, why, and who backed it — never split across pages.
 *  Labelled, since it is read by people with no room to look back at. */
function Fix({
  entry,
  number,
  panel,
}: {
  entry: FixListEntry;
  number: number;
  panel: Map<string, Persona>;
}) {
  const change = entry.issue.recommendation.trim();
  const why = entry.issue.description.trim();
  const blocker = entry.issue.severity === 'Critical';
  const { effort, impact, locationName } = entry.issue;
  const facts = [
    entry.issue.severity,
    locationName && locationName !== 'Page-level' ? locationName : null,
    impact ? `${LEVEL[impact] ?? impact} impact` : null,
    effort ? `${LEVEL[effort] ?? effort} effort` : null,
  ].filter(Boolean);
  const quotes = entry.issue.evidence ?? [];

  return (
    <li className="flex break-inside-avoid gap-4 py-7">
      <span
        className={[
          'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full text-tag font-semibold tabular-nums',
          blocker
            ? 'bg-critical text-white'
            : 'bg-warm-alt text-text-secondary ring-1 ring-hairline',
        ].join(' ')}
      >
        {number}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-subtitle leading-snug font-semibold text-text-primary">
          {change || why}
        </p>
        {facts.length > 0 ? (
          <p className="mt-1.5 text-caption text-text-muted">
            {facts.join(' · ')}
          </p>
        ) : null}
        {change && why && change !== why ? (
          <section className="mt-4">
            <h3 className="!m-0 !text-small-label !font-semibold !text-text-muted uppercase">
              Why fix it
            </h3>
            <p className="mt-1 text-ui leading-relaxed text-text-secondary">
              {blocker ? (
                <span className="font-semibold text-critical">
                  Blocker — it stopped someone going on.{' '}
                </span>
              ) : null}
              {why}
            </p>
          </section>
        ) : null}
        {quotes.length > 0 ? (
          <section className="mt-4">
            <h3 className="!m-0 !text-small-label !font-semibold !text-text-muted uppercase">
              Who said it
            </h3>
            <ul className="mt-2 space-y-2.5 border-l-2 border-hairline-alt pl-4">
              {quotes.map((said) => {
                const role = panel.get(said.personaId)?.title;
                return (
                  <li
                    className="text-ui leading-relaxed text-text-secondary"
                    key={`${said.personaId}:${said.messageId}`}
                  >
                    “{said.quote}”
                    <span className="mt-0.5 block text-caption text-text-muted">
                      —{' '}
                      <span className="font-semibold text-text-primary">
                        {said.personaName}
                      </span>
                      {role ? `, ${role}` : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
      </div>
    </li>
  );
}
